import React, { useEffect } from "react";
import { createPortal } from "react-dom";

export default function Modal({ open, title, onClose, children, footer }) {
  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);
    // prevent background scroll when modal is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev || "";
    };
  }, [open, onClose]);

  if (!open) return null;

  // Portal straight to <body> so the modal escapes any ancestor's stacking
  // context / overflow (e.g. the dashboard shell's fixed header + scrollable
  // main content region), rather than being painted inside them.
  // The codebase module's CSS is scoped as ".cb-page .cb-modal-backdrop" (a
  // descendant selector), so a wrapping ".cb-page" ancestor is required here
  // too — a portaled node is no longer a DOM descendant of the page's own
  // ".cb-page" wrapper.
  // NOTE: classes are prefixed "cb-modal*" (not the bare "modal"/"modal-body"
  // etc.) because other modules in this app (see src/styles/base.css, used by
  // the RAG pages) define their own unscoped ".modal"/".modal-header" rules
  // for a different modal pattern. Since Vite-imported CSS stays live for the
  // whole SPA session once any page that imports it has been visited, a bare
  // ".modal" class here would silently pick up unrelated flex-alignment
  // rules from whichever other module's CSS happened to load first.
  return createPortal(
    <div className="cb-page">
      <div className="cb-modal-backdrop" role="dialog" aria-modal="true">
        {/* overlay click closes */}
        <div className="cb-modal-overlay" onClick={onClose} />

        {/* modal panel */}
        <div
          className="cb-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="cb-modal-header">
            <div className="cb-modal-title">{title}</div>

            <button
              className="cb-modal-close"
              onClick={onClose}
              aria-label="Close"
              title="Close"
              type="button"
            >
              ✕
            </button>
          </div>

          <div className="cb-modal-body">{children}</div>

          {footer ? <div className="cb-modal-footer">{footer}</div> : null}
        </div>
      </div>
    </div>,
    document.body
  );
}
